import { API_BASE_URL } from "@/services";
import type { SSEEvent, AgentId, Channel, ChartSpec, UIGroup } from "@/types/assistant";

// Re-export types from queries for backwards compatibility
export type { 
  ConversationMeta, 
  ConversationMessage, 
  ConversationMessageMetadata,
  MessageChartData 
} from "@/services/queries/assistantQueries";

// Re-export REST functions for backwards compatibility
export { 
  listConversations, 
  getConversationMessages, 
  deleteConversation, 
  getUsage 
} from "@/services/queries/assistantQueries";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type ChatMessage = {
  id: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string;
};

export interface StreamHandlers {
  // Lifecycle
  onStreamStart?: (conversationId: string) => void;
  onStreamEnd?: (latencyMs?: number) => void;

  // Agent lifecycle
  onAgentPlan?: (agents: AgentId[]) => void;
  onAgentStart?: (agent: AgentId, displayName: string, uiGroup: UIGroup) => void;
  onAgentDraft?: (agent: AgentId, text: string) => void;
  onAgentDone?: (agent: AgentId, ok: boolean, irrelevant: boolean, error?: string) => void;

  // Streaming content
  onToken?: (delta: string, agent: AgentId, channel: Channel) => void;
  onThinking?: (delta: string | undefined, text: string | undefined, agent: AgentId) => void;

  // Tool events
  onToolCalling?: (
    id: string,
    name: string,
    agent: AgentId,
    argsPreview?: string,
    args?: Record<string, unknown>,
    server?: string,
    url?: string
  ) => void;
  onToolCompleted?: (
    id: string,
    name: string,
    agent: AgentId,
    ok: boolean,
    elapsedMs?: number,
    resultPreview?: string,
    payload?: Record<string, unknown>,
    server?: string,
    url?: string
  ) => void;

  // Charts & final message
  onCharts?: (charts: ChartSpec[]) => void;
  onFinalMessage?: (text: string) => void;

  // Abort signal
  signal?: AbortSignal;
}

export interface StreamOptions {
  conversationId?: string | undefined;
  authToken?: string | undefined;
  agent?: "quick_chat" | undefined;
  /** Unified context for thesis-aware AI */
  context?: Record<string, unknown> | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Streaming Service
// ─────────────────────────────────────────────────────────────────────────────

const STREAM_ENDPOINT = `${API_BASE_URL}/ai/conversations/send/stream`;

/**
 * AssistantService handles SSE streaming for multi-agent responses.
 * For REST operations, use functions from @/services/queries/assistantQueries.
 */
export class AssistantService {
  /**
   * Stream a multi-agent response
   * @param history - Chat message history
   * @param handlers - Stream event handlers
   * @param options - Streaming options (conversationId, authToken, agent mode)
   * @returns The conversation ID used for this stream
   */
  async streamMultiAgent(
    history: ChatMessage[],
    handlers: StreamHandlers,
    options?: StreamOptions
  ): Promise<string> {
    const lastUser = [...history].reverse().find((m) => m.role === "user");
    const userInput = lastUser?.content?.trim() || "";
    if (!userInput) throw new Error("No user input provided");

    const conversationId = options?.conversationId ?? crypto.randomUUID();

    const body: Record<string, unknown> = {
      conversation_id: conversationId,
      user_input: userInput,
    };

    if (options?.agent) {
      body["agent"] = options.agent;
    }

    // Include unified context for thesis-aware AI
    if (options?.context) {
      body["context"] = options.context;
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options?.authToken ? { Authorization: `Bearer ${options.authToken}` } : {}),
    };

    const res = await fetch(STREAM_ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: handlers.signal ?? null,
    });

    if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);

        const event = this.parseSSE(raw);
        if (event) this.dispatch(event, handlers);
      }
    }
    
    return conversationId;
  }

  private parseSSE(raw: string): SSEEvent | null {
    let eventName: string | undefined;
    const dataLines: string[] = [];

    for (const line of raw.split("\n")) {
      if (line.startsWith("event:")) eventName = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    }

    if (!dataLines.length || !eventName) return null;

    try {
      const data = JSON.parse(dataLines.join("\n"));
      return { event: eventName, data } as SSEEvent;
    } catch {
      // Silently ignore parse failures - these are typically incomplete chunks
      return null;
    }
  }

  private dispatch(event: SSEEvent, h: StreamHandlers): void {
    switch (event.event) {
      case "stream.start":
        h.onStreamStart?.(event.data.conversation_id);
        break;

      case "agent.plan":
        h.onAgentPlan?.(event.data.agents);
        break;

      case "agent.start":
        h.onAgentStart?.(event.data.agent, event.data.display_name, event.data.ui.group ?? "analysis");
        break;

      case "agent.draft":
        h.onAgentDraft?.(event.data.agent, event.data.text);
        break;

      case "agent.done":
        h.onAgentDone?.(event.data.agent, event.data.ok, event.data.irrelevant, event.data.error);
        break;

      case "token":
        h.onToken?.(event.data.delta, event.data.agent, event.data.channel);
        break;

      case "thinking":
        h.onThinking?.(event.data.delta, event.data.text, event.data.agent);
        break;

      case "tool":
        if (event.data.phase === "calling") {
          h.onToolCalling?.(
            event.data.id, event.data.name, event.data.agent,
            event.data.args_preview, event.data.args, event.data.server, event.data.url
          );
        } else {
          h.onToolCompleted?.(
            event.data.id, event.data.name, event.data.agent, event.data.ok,
            event.data.elapsed_ms, event.data.result_preview, event.data.payload,
            event.data.server, event.data.url
          );
        }
        break;

      case "chart.dsl":
        h.onCharts?.(event.data.charts);
        break;

      case "message":
        h.onFinalMessage?.(event.data.text);
        break;

      case "stream.end":
        h.onStreamEnd?.();
        break;

      default:
        // Unknown event types are silently ignored
        break;
    }
  }
}

export default AssistantService;
